import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Loader2, Plus, Trash2 } from "lucide-react";
import {
  clientsService,
  type S3RetentionRule,
  type S3RetentionSimulation,
  type S3RetentionStatus,
} from "../services/api";
import { SURFACE, TONE_BADGE, TONE_BORDER, TONE_TEXT } from "../utils/statusColors";
import { lerSecao, mensagemDoErroDeCarga } from '../design';
import { useConfirmarPerigo } from './shared/ConfirmDialog';
import { estadoDoBucket, fraseDoBucketTravado } from '../utils/s3Bucket';

/**
 * Retenção de áudios S3 do cliente.
 *
 * Duas coisas separam esta tela da versão anterior:
 *
 * 1. **Flag explícita.** A coluna `s3RetentionDays` tem DEFAULT 365, então a
 *    ficha mostrava "1 ano" para 184 dos 187 clientes sem que ninguém tivesse
 *    decidido isso — e um Salvar em qualquer outro campo transformava esse
 *    default em exclusão real. Na INTERSEPT (2 anos por contrato) seriam
 *    84,4 GiB. Agora campo preenchido não significa regra valendo.
 *
 * 2. **Linhas por pasta.** O que varia entre clientes é o prefixo, não o
 *    bucket: 115 buckets têm só `monitor/`, 9 têm `monitor/` e `storage1/`
 *    (131 GiB que ficavam fora de qualquer regra), nenhum tem só `storage1/`.
 *
 * A gravação tem endpoint próprio e botão próprio, fora do Salvar do modal, de
 * propósito: apagar acervo de cliente não pode ser efeito colateral de editar
 * um e-mail de contato.
 */

const RETENTION_OPTIONS = [
  { value: 365, label: "1 ano (365 dias)" },
  { value: 730, label: "2 anos (730 dias)" },
  { value: 1095, label: "3 anos (1095 dias)" },
  { value: 1825, label: "5 anos (1825 dias)" },
  { value: 3650, label: "10 anos (3650 dias)" },
  { value: 7300, label: "20 anos (7300 dias)" },
];

/** Pastas de áudio conhecidas: `monitor/` é o formato atual, `storage1/` o antigo. */
const KNOWN_AUDIO_PREFIXES = ["monitor/", "storage1/"];

function formatBytes(bytes: number): string {
  if (!bytes) return "0 B";
  const units = ["B", "KiB", "MiB", "GiB", "TiB"];
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  return `${(bytes / 1024 ** i).toFixed(i >= 3 ? 1 : 0)} ${units[i]}`;
}

function formatNumber(n: number): string {
  return n.toLocaleString("pt-BR");
}

interface Props {
  clientId?: number;
  /** Nome da instância EC2, usado como bucket quando nenhum foi escolhido. */
  ec2InstanceName?: string;
  onSaved?: () => void;
}

export default function S3RetentionSection({
  clientId,
  ec2InstanceName,
  onSaved,
}: Props) {
  const confirmarPerigo = useConfirmarPerigo();
  const [status, setStatus] = useState<S3RetentionStatus | null>(null);
  const [buckets, setBuckets] = useState<string[]>([]);
  // A lista de buckets da conta AWS NÃO CARREGOU (08/10/2026): antes `.catch(() => [])` e a tela dizia
  // "Carregado da conta AWS" sem ter carregado. O bucket atual segue visível; trocar, só com a lista.
  const [bucketsErro, setBucketsErro] = useState<string | null>(null);
  const [prefixesNoBucket, setPrefixesNoBucket] = useState<string[]>([]);
  // D1 (11/10/2026): o load era try/finally SEM catch. Na falha, o form ficava nos defaults (desligada, monitor/ 365
  // dias) e parecia cliente sem retenção; ligar e aplicar gravava a regra padrão POR CIMA da que está na AWS.
  const [statusErro, setStatusErro] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [bucket, setBucket] = useState("");
  const [naoSeAplica, setNaoSeAplica] = useState(false);
  const [motivoNA, setMotivoNA] = useState("");
  // Ref porque `simulate` é declarado acima de onde `bucketAtual` é calculado,
  // e a simulação precisa medir o bucket que está selecionado AGORA, não o
  // que está salvo — senão só dá para conferir depois de aplicar, que é o
  // passo irreversível.
  const bucketAtualRef = useRef("");
  const [rules, setRules] = useState<S3RetentionRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{
    tone: "success" | "danger";
    text: string;
  } | null>(null);
  /** Linhas em que o usuário escolheu digitar o prazo à mão. */
  const [prazoLivre, setPrazoLivre] = useState<Record<number, boolean>>({});
  const [simulations, setSimulations] = useState<
    Record<string, S3RetentionSimulation | "loading">
  >({});

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const [st, bk] = await Promise.all([
        clientsService.getS3Retention(clientId),
        lerSecao(clientsService.listS3Buckets()),
      ]);
      setStatus(st);
      setStatusErro(null);
      setBucketsErro(bk.erro);
      setBuckets(Array.isArray(bk.valor) ? (bk.valor as string[]) : []);
      setEnabled(st.enabled);
      setBucket(st.bucket || "");
      setNaoSeAplica(!!st.notApplicable);
      setMotivoNA(st.notApplicableReason || "");
      setRules(
        st.rules.length
          ? st.rules.map((r) => ({ ...r }))
          : [{ prefix: "monitor/", retentionDays: 365, glacierDays: 90 }],
      );
      if (st.bucketExists) {
        setPrefixesNoBucket(
          await clientsService.listS3Prefixes(clientId).catch(() => []),
        );
      }
    } catch (e) {
      setStatus(null);
      setStatusErro(mensagemDoErroDeCarga(e));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    void load();
  }, [load]);

  const updateRule = (index: number, patch: Partial<S3RetentionRule>) => {
    setRules((prev) =>
      prev.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    );
    setSimulations({});
  };

  const simulate = async (index: number) => {
    if (!clientId) return;
    const rule = rules[index];
    setSimulations((prev) => ({ ...prev, [rule.prefix]: "loading" }));
    try {
      const result = await clientsService.simulateS3Retention(
        clientId,
        rule,
        bucketAtualRef.current,
      );
      setSimulations((prev) => ({ ...prev, [rule.prefix]: result }));
    } catch (error: any) {
      setSimulations((prev) => {
        const next = { ...prev };
        delete next[rule.prefix];
        return next;
      });
      setMessage({
        tone: "danger",
        text: error?.response?.data?.message || "Falha ao simular",
      });
    }
  };

  /**
   * "Não se aplica" grava na hora, no próprio toggle.
   *
   * Ter dois botões de salvar na mesma tela já custou caro: o Salvar do modal
   * não carrega este campo (a seção fala com um endpoint próprio), então marcar
   * e salvar não gravava nada e nada avisava. Como a marcação não apaga acervo,
   * ela não precisa de botão — só de confirmação quando há regra valendo.
   */
  const marcarNaoSeAplica = async (valor: boolean) => {
    if (!clientId) return;
    if (valor && status?.appliedRuleIds.length) {
      const ok = await confirmarPerigo(
        `Marcar como sem retenção aplicável vai REMOVER as regras que estão valendo em ` +
          `s3://${bucketAtualRef.current || ec2InstanceName}.\n\n` +
          "Use isto só quando o cliente realmente não tem mais gravação em nuvem.",
      );
      if (!ok) return;
    }
    setNaoSeAplica(valor);
    if (valor) setEnabled(false);
    setSaving(true);
    setMessage(null);
    try {
      const result = await clientsService.saveS3Retention(
        clientId,
        valor ? false : enabled,
        rules,
        bucket,
        {
          notApplicable: valor,
          notApplicableReason: valor ? motivoNA : undefined,
        },
      );
      setMessage({
        tone: result?.success === false ? "danger" : "success",
        text: valor
          ? "Marcado como sem retenção aplicável."
          : "Desmarcado. Este cliente volta para a fila de configuração.",
      });
      await load();
      onSaved?.();
    } catch (error: any) {
      setNaoSeAplica(!valor);
      setMessage({
        tone: "danger",
        text: error?.response?.data?.message || "Falha ao salvar",
      });
    } finally {
      setSaving(false);
    }
  };

  /** Grava só o motivo, quando o campo perde o foco. */
  const salvarMotivo = async () => {
    if (!clientId || !naoSeAplica) return;
    if ((status?.notApplicableReason || "") === motivoNA) return;
    try {
      await clientsService.saveS3Retention(clientId, false, rules, bucket, {
        notApplicable: true,
        notApplicableReason: motivoNA,
      });
      await load();
    } catch (e) {
      /* o motivo é acessório: falhar aqui não deve travar a tela */
    }
  };

  const save = async () => {
    if (!clientId) return;
    // Nome do alvo só para as mensagens de confirmação. NÃO chamar de `bucket`:
    // sombreava o estado do select, e o `save` acabava mandando o bucket salvo
    // em vez do escolhido — a tela dizia cartsys-2 e o backend recebia cartsys-1.
    const alvo = bucketAtualRef.current || ec2InstanceName;

    // Marcar "não se aplica" com regra valendo remove as regras da AWS.
    if (naoSeAplica && status?.appliedRuleIds.length) {
      const ok = await confirmarPerigo(
        `Marcar como sem retenção aplicável vai REMOVER as regras que estão valendo em s3://${alvo}.\n\n` +
          "Use isto só quando o cliente realmente não tem mais gravação em nuvem.",
      );
      if (!ok) return;
    }

    // Desligar remove as regras que estão valendo na AWS. No estado mais comum
    // desta tela — flag desligada porque nasceu assim, mas bucket com regra real
    // aplicada — um clique distraído tiraria a retenção do cliente sem avisar.
    if (!enabled && status?.appliedRuleIds.length) {
      const quais = (status.appliedRules || [])
        .map((r) => `${r.prefix} (${r.retentionDays ?? "?"} dias)`)
        .join(", ");
      const ok = await confirmarPerigo(
        `Remover a retenção que está VALENDO em s3://${alvo}?\n\n${quais}\n\n` +
          "O cliente deixa de ter exclusão automática e o acervo volta a crescer sem limite. " +
          "Nada do que já foi apagado volta.",
      );
      if (!ok) return;
    }

    // Confirmação ao ligar: apaga acervo. Ligar já simulado mostra o número
    // real; sem simulação, avisa que ele é desconhecido.
    if (enabled) {
      const totalExpiring = rules.reduce((acc, r) => {
        const sim = simulations[r.prefix];
        return acc + (sim && sim !== "loading" ? sim.expiring.objects : 0);
      }, 0);
      const detalhe = Object.keys(simulations).length
        ? `Serão apagados ${formatNumber(totalExpiring)} objetos.`
        : "O volume não foi simulado, então não dá para saber quanto será apagado.";
      const ok = await confirmarPerigo(
        `Ligar a retenção em s3://${alvo}?\n\n${detalhe}\n\nA exclusão é definitiva e começa em até 48h.`,
      );
      if (!ok) return;
    }

    setSaving(true);
    setMessage(null);
    try {
      const result = await clientsService.saveS3Retention(
        clientId,
        enabled,
        rules,
        bucket,
        {
          notApplicable: naoSeAplica,
          notApplicableReason: naoSeAplica ? motivoNA : undefined,
        },
      );
      const ok = result?.success !== false;
      setMessage({
        tone: ok ? "success" : "danger",
        text: result?.message || "Retenção atualizada",
      });
      // Só recarrega quando deu certo. Recarregar depois de uma falha
      // descartava silenciosamente o bucket que o usuário tinha escolhido no
      // select, e o clique seguinte reenviava o nome antigo como se fosse
      // escolha dele.
      if (ok) {
        await load();
        onSaved?.();
      }
    } catch (error: any) {
      setMessage({
        tone: "danger",
        text: error?.response?.data?.message || "Falha ao salvar",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!clientId) {
    return (
      <div className="border-t border-slate-700 pt-4 mt-2">
        <label className="block text-sm font-medium text-slate-300 mb-3">
          Configuração S3 (Retenção de Áudios)
        </label>
        <div className={`${SURFACE.panel} p-3`}>
          <p className="text-xs text-slate-400">
            Salve o cliente primeiro. A retenção precisa do bucket já cadastrado
            para conferir o que existe na AWS antes de apagar qualquer coisa.
          </p>
        </div>
      </div>
    );
  }

  // D1: sem a leitura da AWS, nada aqui é o que está lá: interruptor, regras e Aplicar não aparecem.
  if (!status) {
    return (
      <div className="border-t border-slate-700 pt-4 mt-2">
        <label className="block text-sm font-medium text-slate-300 mb-3">
          Configuração S3 (Retenção de Áudios)
        </label>
        <div className={`${SURFACE.panel} p-3 space-y-2`}>
          {statusErro ? (
            <>
              <p role="alert" className={`text-sm ${TONE_TEXT.danger}`}>
                Não consegui ler a retenção deste cliente ({statusErro}): nada aqui é o que está na AWS, então não dá
                para ligar nem aplicar agora.
              </p>
              <button
                type="button"
                onClick={() => void load()}
                className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-xs"
              >
                Tentar de novo
              </button>
            </>
          ) : (
            <p className="text-xs text-slate-400">Lendo a retenção na AWS…</p>
          )}
        </div>
      </div>
    );
  }

  const bucketAtual = bucket || ec2InstanceName || "";
  // Bucket trocado no select e ainda não gravado. O Simular e o aviso de
  // "não encontrado" olham o bucket SALVO, então enquanto não aplicar eles
  // falariam do bucket errado.
  const bucketPendente = !!status && bucket !== (status.bucket || "");
  bucketAtualRef.current = bucketAtual;
  // 12/10/2026 (conferência em tela): `bucketExists: false` NÃO quer dizer "o bucket não existe". O backend
  // (clients.service getS3Retention) trata QUALQUER erro da AWS assim: credencial vencida, AccessDenied, throttling.
  // Com a AWS fora, a seção abria dizendo "nenhuma regra na AWS" e deixava ligar e Aplicar a regra padrão por cima da
  // real. Até o backend separar NoSuchBucket dos outros erros, o bucket cadastrado e não confirmado trava o Aplicar.
  // Com a #405 o servidor separa "não existe" de "não deu para saber" (utils/s3Bucket); sem ela, segue a regra acima.
  const estado = status ? estadoDoBucket(status) : 'confirmado';
  const bucketSumido = !!status && !!status.bucket && estado !== 'confirmado' && !bucketPendente;
  const bucketInexistente = bucketSumido && estado === 'inexistente';
  const FRASE_DO_BUCKET_NAO_CONFIRMADO = fraseDoBucketTravado(estado, status?.bucket || '', status?.bucketCheckMotivo);
  const descobertas = (status?.uncoveredPrefixes || []).filter(
    (p) => !rules.some((r) => r.prefix === p),
  );
  const sugestoes = prefixesNoBucket.filter(
    (p) =>
      KNOWN_AUDIO_PREFIXES.includes(p) && !rules.some((r) => r.prefix === p),
  );

  return (
    <div className="border-t border-slate-700 pt-4 mt-2 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <label className="block text-sm font-medium text-slate-300">
          Configuração S3 (Retenção de Áudios)
        </label>
        {/* Some quando o cliente está marcado como "não se aplica": não faz
            sentido oferecer ligar retenção para quem não tem gravação. Desmarcar
            "não se aplica" traz o toggle de volta. */}
        <div
          className={`flex items-center gap-3 shrink-0 ${naoSeAplica ? "hidden" : ""}`}
        >
          {/* Mesmo toggle dos demais interruptores do modal (Orgao Publico,
              Incluir XML, ...): esta secao nao pode parecer de outro sistema. */}
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-orange-500"></div>
          </label>
          <span className="text-sm text-slate-300">Retenção ativa</span>
          {/* O badge lê a AWS, não a flag: 6 clientes têm regra gravada e vão
              nascer com a flag desligada, e dizer "nenhuma regra" seria falso. */}
          {status && (
            <span
              className={`px-2 py-0.5 rounded text-xs ${
                TONE_BADGE[bucketInexistente ? "danger" : bucketSumido ? "warning" : status.appliedRuleIds.length ? "success" : "neutral"]
              }`}
            >
              {bucketInexistente
                ? "bucket não existe"
                : bucketSumido
                ? "bucket não confirmado"
                : status.appliedRuleIds.length
                  ? `${status.appliedRuleIds.length} regra(s) na AWS`
                  : "nenhuma regra na AWS"}
            </span>
          )}
        </div>
      </div>

      {/* Some quando a retenção está ligada: os dois estados são
          contraditórios, e deixar a marcação visível ali convida a ligar as
          duas e depois descobrir qual venceu. */}
      {!enabled && (
        <>
          {/* "Não se aplica" é o que separa "ninguém configurou ainda" de "não há o
          que configurar". Sem essa marcação a lista não consegue distinguir um
          cadastro com bucket errado de um cliente que saiu da nuvem. */}
          <div className="flex items-start gap-3">
            <label className="relative inline-flex items-center cursor-pointer mt-0.5">
              <input
                type="checkbox"
                checked={naoSeAplica}
                onChange={(e) => marcarNaoSeAplica(e.target.checked)}
                disabled={saving}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-slate-500"></div>
            </label>
            <div className="flex-1">
              <span className="text-sm text-slate-300">Não se aplica</span>
              <p className="text-xs text-slate-500">
                Cliente sem gravação em nuvem: servidor local, só STFC pelo SBC,
                contrato encerrado.
              </p>
              {naoSeAplica && (
                <input
                  type="text"
                  value={motivoNA}
                  onChange={(e) => setMotivoNA(e.target.value)}
                  onBlur={salvarMotivo}
                  placeholder="Motivo (ex: migrou para servidor local em 2025)"
                  maxLength={160}
                  className={`w-full mt-2 px-3 py-2 ${SURFACE.input} text-white text-sm`}
                />
              )}
            </div>
          </div>
        </>
      )}

      {!enabled && !naoSeAplica && !status?.appliedRuleIds.length && (
        <p className="text-xs text-slate-500">
          Retenção desligada: o HUB não escreve nenhuma regra de exclusão na AWS
          e o acervo deste cliente cresce sem limite. Ligue a flag para
          configurar as pastas.
        </p>
      )}

      {/* Cadastro e AWS discordando. Acontece com quem já tinha regra aplicada
          pelo botão antigo: a flag nasce desligada para todo mundo, porque
          ligá-la de volta é decisão de quem conferiu o prazo no contrato. */}
      {!status?.enabled && !!status?.appliedRuleIds.length && (
        <div className={`${SURFACE.panel} p-3 border-amber-600/60`}>
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-amber-400 font-medium">
                Cadastro e AWS discordam
              </p>
              <p className="text-xs text-slate-400 mt-1">
                A flag está desligada, mas o bucket já tem regra valendo:{" "}
                <span className="font-mono text-slate-300">
                  {(status.appliedRules || []).length
                    ? status.appliedRules
                        .map(
                          (r) => `${r.prefix} ${r.retentionDays ?? "?"} dias`,
                        )
                        .join(", ")
                    : status.appliedRuleIds.join(", ")}
                </span>
                . Confira o prazo no contrato: se bate, ligue a flag; se não,
                ajuste as pastas abaixo antes de ligar.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* O formulário só aparece com a flag ligada. Desligada, ele era um convite
          a preencher prazo que não vale nada — e foi justamente o campo visível
          com 365 que quase apagou 84,4 GiB da INTERSEPT. */}
      {enabled && !naoSeAplica && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1">
                Bucket S3
              </label>
              {buckets.length > 0 ? (
                <select
                  value={bucketAtual}
                  onChange={(e) => setBucket(e.target.value)}
                  className={`w-full px-3 py-2 ${SURFACE.input} text-white text-sm`}
                >
                  <option value="">
                    (usar o nome da instância:{" "}
                    {ec2InstanceName || "não definido"})
                  </option>
                  {buckets.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              ) : (
                <div
                  className={`w-full px-3 py-2 ${SURFACE.input} text-slate-400 text-sm font-mono`}
                >
                  {bucketAtual || "(nenhum)"}
                </div>
              )}
              {bucketsErro && (
                <p role="alert" className="text-xs text-amber-400 mt-1" data-buckets="nao-carregaram">
                  A lista de buckets da conta AWS não carregou ({bucketsErro}): o bucket atual está acima, e trocá-lo só
                  depois que a lista carregar.
                </p>
              )}
              <p className="text-xs text-slate-500 mt-1">
                Carregado da conta AWS. 33 clientes apontavam para bucket que
                não existe porque a instância foi recriada e o sufixo mudou.
              </p>
              {bucketPendente && (
                <p className="text-xs text-amber-400 mt-1">
                  Bucket alterado. Clique em{" "}
                  {naoSeAplica
                    ? "Marcar como não aplicável"
                    : enabled
                      ? "Aplicar retenção"
                      : "Desligar retenção"}{" "}
                  para gravar a escolha; até lá o Simular mediria o bucket
                  antigo.
                </p>
              )}
            </div>

            {/* Vermelho só quando a AWS AFIRMOU que não existe; "não confirmado" é âmbar, com o motivo do servidor
                (afirmar "não existe" por uma credencial vencida era a mentira que a #460 e a #405 tiram). */}
            {bucketSumido && (
              <div className={`${SURFACE.panel} p-3 ${bucketInexistente ? TONE_BORDER.danger : TONE_BORDER.warning}`} data-testid="caixa-do-bucket">
                <div className="flex items-start gap-2">
                  <AlertTriangle className={`w-4 h-4 shrink-0 mt-0.5 ${bucketInexistente ? TONE_TEXT.danger : TONE_TEXT.warning}`} />
                  <div>
                    <p className={`text-sm font-medium ${bucketInexistente ? TONE_TEXT.danger : TONE_TEXT.warning}`}>
                      {bucketInexistente ? "Bucket não existe na conta" : "Bucket não confirmado"}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      <span className="font-mono">{status?.bucket}</span>
                      {bucketInexistente
                        ? ": a AWS respondeu que ele não existe. Nenhuma regra é aplicada."
                        : status?.bucketCheckMotivo
                          ? `: ${status.bucketCheckMotivo}. Nenhuma regra é aplicada até confirmar.`
                          : ": não existe na conta, ou a leitura da AWS falhou. Nenhuma regra é aplicada até confirmar."}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="grid grid-cols-[1.2fr_1.4fr_0.8fr_auto] gap-2 px-1">
              <span className="text-xs text-slate-400">Pasta</span>
              <span className="text-xs text-slate-400">
                Política de Retenção
              </span>
              <span className="text-xs text-slate-400">Glacier IR (dias)</span>
              <span className="w-16" />
            </div>

            {rules.map((rule, index) => {
              const sim = simulations[rule.prefix];
              // Prazo à mão: ou o usuário pediu, ou o valor salvo não é nenhum preset.
              const livre =
                prazoLivre[index] ??
                !RETENTION_OPTIONS.some((o) => o.value === rule.retentionDays);
              // Glacier cobra 90 dias mínimos por objeto: transicionar e apagar
              // dentro dessa janela custa mais do que nunca ter transicionado.
              const janelaGlacier =
                rule.retentionDays && rule.glacierDays
                  ? rule.retentionDays - rule.glacierDays
                  : null;
              return (
                <div key={index} className="space-y-1">
                  <div className="grid grid-cols-[1.2fr_1.4fr_0.8fr_auto] gap-2 items-center">
                    <input
                      type="text"
                      value={rule.prefix}
                      onChange={(e) =>
                        updateRule(index, { prefix: e.target.value })
                      }
                      placeholder="monitor/"
                      className={`w-full px-3 py-2 ${SURFACE.input} text-white text-sm font-mono`}
                    />
                    <div className="space-y-1">
                      <select
                        value={livre ? "livre" : rule.retentionDays}
                        onChange={(e) => {
                          if (e.target.value === "livre") {
                            setPrazoLivre((p) => ({ ...p, [index]: true }));
                            return;
                          }
                          setPrazoLivre((p) => ({ ...p, [index]: false }));
                          updateRule(index, {
                            retentionDays: parseInt(e.target.value, 10),
                          });
                        }}
                        className={`w-full px-3 py-2 ${SURFACE.input} text-white text-sm`}
                      >
                        {RETENTION_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                        <option value="livre">Outro prazo (digitar)</option>
                      </select>
                      {livre && (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={1}
                            value={rule.retentionDays || ""}
                            onChange={(e) =>
                              updateRule(index, {
                                retentionDays: parseInt(e.target.value, 10) || 0,
                              })
                            }
                            placeholder="dias"
                            autoFocus
                            className={`w-24 px-2 py-1 ${SURFACE.input} text-white text-sm`}
                          />
                          <span className="text-xs text-slate-400">
                            {rule.retentionDays
                              ? `${(rule.retentionDays / 365).toFixed(1)} anos`
                              : "dias"}
                          </span>
                        </div>
                      )}
                    </div>
                    <input
                      type="number"
                      value={rule.glacierDays ?? 90}
                      onChange={(e) =>
                        updateRule(index, {
                          glacierDays: parseInt(e.target.value, 10) || 90,
                        })
                      }
                      className={`w-full px-3 py-2 ${SURFACE.input} text-white text-sm`}
                    />
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => simulate(index)}
                        disabled={sim === "loading" || !bucketAtual}
                        title="Contar o que essa regra apagaria, sem aplicar nada"
                        className="px-2 py-2 text-xs text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-lg disabled:opacity-40"
                      >
                        {sim === "loading" ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          "Simular"
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setRules((prev) => prev.filter((_, i) => i !== index))
                        }
                        disabled={rules.length === 1}
                        className="p-2 text-slate-400 hover:text-red-400 disabled:opacity-30"
                        title="Remover pasta"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {janelaGlacier !== null && janelaGlacier < 90 && (
                    <div className="ml-1 flex items-start gap-2 text-xs text-amber-300">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>
                        Só {janelaGlacier} dias entre ir para o Glacier e ser
                        apagado. O Glacier cobra no mínimo 90 dias por objeto,
                        então esse trecho sai mais caro do que ficar no Standard.
                        Suba a retenção ou baixe o Glacier IR para{" "}
                        {Math.max(1, rule.retentionDays - 90)} dias.
                      </span>
                    </div>
                  )}

                  {sim && sim !== "loading" && (
                    <div className={`${SURFACE.panel} p-3 ml-1`}>
                      {sim.unsupportedLayout ? (
                        <p className="text-xs text-amber-400">
                          Esta pasta não é organizada por dia, então não dá para
                          medir sem varrer o bucket inteiro. Confira o conteúdo
                          pelo console da AWS antes de ligar.
                        </p>
                      ) : (
                        <>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <div className="text-xs text-slate-400">
                                Seria apagado
                              </div>
                              <div className="text-lg font-bold text-red-400">
                                {formatBytes(sim.expiring.bytes)}
                              </div>
                              <div className="text-xs text-slate-500">
                                {formatNumber(sim.expiring.objects)} objetos
                                &middot; {formatNumber(sim.expiring.folders)}{" "}
                                dias
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-slate-400">
                                Vai para Glacier IR
                              </div>
                              <div className={`text-lg font-bold ${TONE_TEXT.info}`}>
                                {formatBytes(sim.transitioning.bytes)}
                              </div>
                              <div className="text-xs text-slate-500">
                                {formatNumber(sim.transitioning.objects)}{" "}
                                objetos
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-slate-400">
                                Fica no Standard
                              </div>
                              <div className="text-lg font-bold text-slate-300">
                                {formatBytes(sim.stayingStandard.bytes)}
                              </div>
                              <div className="text-xs text-slate-500">
                                {formatNumber(sim.stayingStandard.objects)}{" "}
                                objetos
                              </div>
                            </div>
                          </div>

                          {/* O intervalo de datas é exato mesmo quando o volume é
                          estimado: ele sai do nome da pasta, não da varredura.
                          É o dado que pega o erro de prazo antes de apagar. */}
                          {sim.expiring.folders > 0 && (
                            <p className="text-xs text-red-400/90 mt-2">
                              Apaga tudo de <strong>{sim.expiring.from}</strong>{" "}
                              até <strong>{sim.expiring.to}</strong>. Confira
                              contra o prazo do contrato.
                            </p>
                          )}

                          <p className="text-xs text-slate-500 mt-2">
                            {sim.oldestKeyDate &&
                              `Gravação mais antiga: ${sim.oldestKeyDate}. `}
                            Objeto abaixo de 128 KiB não vai para o Glacier IR.
                            {sim.estimated && (
                              <span className="text-amber-400">
                                {" "}
                                Volume estimado a partir de{" "}
                                {formatNumber(sim.sampledFolders)} dias medidos
                                de {formatNumber(sim.totalFolders)}. As datas
                                acima são exatas.
                              </span>
                            )}
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            <button
              type="button"
              onClick={() =>
                setRules((prev) => [
                  ...prev,
                  { prefix: "", retentionDays: 365, glacierDays: 90 },
                ])
              }
              className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-slate-200 border border-dashed border-slate-700 hover:border-slate-500 rounded-lg w-full justify-center"
            >
              <Plus className="w-4 h-4" /> adicionar pasta
            </button>
          </div>

          {(descobertas.length > 0 || sugestoes.length > 0) && (
            <div className={`${SURFACE.panel} p-3 border-amber-600/60`}>
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-amber-400 font-medium">
                    Pasta de áudio sem regra
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    O bucket tem{" "}
                    <span className="font-mono text-slate-300">
                      {Array.from(new Set([...descobertas, ...sugestoes])).join(
                        ", ",
                      )}
                    </span>{" "}
                    sem nenhuma regra. Esse acervo nunca vai para o Glacier IR
                    nem é apagado.
                  </p>
                  <div className="flex gap-2 mt-2">
                    {Array.from(new Set([...descobertas, ...sugestoes])).map(
                      (p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() =>
                            setRules((prev) => [
                              ...prev.filter((r) => r.prefix),
                              {
                                prefix: p,
                                retentionDays: rules[0]?.retentionDays || 365,
                                glacierDays: 90,
                              },
                            ])
                          }
                          className="px-2 py-1 text-xs text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 rounded"
                        >
                          incluir {p}
                        </button>
                      ),
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className={`${SURFACE.panel} p-3`}>
            <p className="text-xs text-slate-400">
              As regras serão aplicadas em:{" "}
              {rules
                .filter((r) => r.prefix)
                .map((r) => (
                  <span
                    key={r.prefix}
                    className="text-slate-300 font-mono mr-3"
                  >
                    s3://{bucketAtual || "bucket"}/{r.prefix.replace(/^\//, "")}
                  </span>
                ))}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Cada pasta leva junto a limpeza de versões antigas, marcadores de
              exclusão e uploads interrompidos, sempre restrita ao mesmo
              prefixo. Nada fora dessas pastas é tocado.
            </p>
          </div>
        </>
      )}

      {message && (
        <div className={`${SURFACE.panel} p-3`}>
          <p
            className={`text-sm ${message.tone === "danger" ? "text-red-400" : "text-emerald-400"}`}
          >
            {message.text}
          </p>
        </div>
      )}

      {/* Nada a fazer quando a flag está desligada e a AWS também está limpa. */}
      {(enabled || !!status?.appliedRuleIds.length) && (
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={save}
            disabled={saving || loading || (enabled && bucketSumido)}
            title={enabled && bucketSumido ? FRASE_DO_BUCKET_NAO_CONFIRMADO : undefined}
            className={`px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2 ${
              enabled
                ? "bg-primary hover:bg-primary-strong text-white"
                : "bg-slate-700 hover:bg-slate-600 text-slate-200"
            }`}
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {enabled ? "Aplicar retenção" : "Desligar retenção"}
          </button>
          {enabled && bucketSumido ? (
            <p role="alert" className={`text-xs ${bucketInexistente ? TONE_TEXT.danger : TONE_TEXT.warning}`} data-testid="bucket-nao-confirmado">
              {FRASE_DO_BUCKET_NAO_CONFIRMADO}
            </p>
          ) : (
            <p className="text-xs text-slate-500">
              Grava direto na AWS, sem depender do Salvar do cliente.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
